import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';

import Avatar from '../../Avatar';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/comment-arrow.svg';
import { ReactComponent as ArrowUpIcon } from '../../../../assets/icons/send.svg';
import styles from './Comment.module.scss';
import Footer from '../CommentFooter';
import { COMMENT } from '../../../../enums/TYPES';
import TrashButton from '../../TrashButton';
import { useAppSelector } from '../../../../store/hooks';
import { TUser } from '../../../../store/user/userSlice';
import { DateFormatter } from '../../../../helpers/dateFormatter';
import CrossIcon from '../../../../assets/icons/cross.svg';
import EditButton from '../../EditButton';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useOnClickOutside } from 'usehooks-ts';
import { MoreIcon } from '../../../../assets/icons/threedots';
import { findSearchValue } from '../../../../helpers';
import { ROLES } from '../../../../enums/ROLES';
import { getCommentDate, isDeletedComment } from '../../../../helpers/comments';

type TCommentReply = {
  comment: COMMENT;
  small?: boolean;
  disable: boolean;
  videofeedId: string;
  fetch: (value: string) => void;
  onDelete: (comment: COMMENT) => void;
  onEditSubmit?: (comment: COMMENT, newMessage: string) => void;
  lastReply: boolean;
  setImpertivlyShowFooter: (b: boolean) => void;
  imperativlyShowFooter: boolean;
  parentDeleted?: boolean;
};

const CommentReply: React.FC<TCommentReply> = ({
  comment,
  small,
  videofeedId,
  fetch,
  onDelete,
  onEditSubmit,
  disable,
  lastReply,
  imperativlyShowFooter,
  setImpertivlyShowFooter,
  parentDeleted = false,
}) => {
  const deleted = isDeletedComment(comment);
  const [showFooter, setShowFooter] = useState(false);
  const notification = useSnackbar();
  const [isEdit, setIsEdit] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const { currentUser, role } = useAppSelector((state: { user: TUser }) => state.user);
  const { videoPlayedSeconds } = useAppSelector((state) => state.videoStore);
  const showDeleteBtn =
    currentUser?.id === comment?.created_by?.id ||
    (findSearchValue('allContent') === 'true' && role === ROLES.ORGANIZATION_ADMIN);
  const footerRef = createRef<HTMLDivElement>();

  const handleCommentDelete = () => {
    onDelete(comment);
  };

  const handleShowFooter = () => {
    if (!lastReply) {
      setImpertivlyShowFooter(!imperativlyShowFooter);
      return;
    }
    setImpertivlyShowFooter(!imperativlyShowFooter);
    setShowFooter(!imperativlyShowFooter);
  };

  const onMsgEdit = (e: any) => {
    setNewMsg(e.target.value);
  };

  const onMsgSubmit = (e: any) => {
    if (!newMsg.trim()) {
      notification.showMessage('Empty message');
      return;
    }
    if (comment.message.trim() !== newMsg.trim() && onEditSubmit) onEditSubmit(comment, newMsg);

    onEditClose();
  };

  useEffect(() => {
    if (lastReply) {
      setShowFooter(imperativlyShowFooter && !parentDeleted && !deleted);
    }
  }, [imperativlyShowFooter, lastReply, parentDeleted, deleted]);

  useEffect(() => {
    if (deleted) {
      setIsEdit(false);
      setNewMsg('');
      setShowDotsMenu(false);
    }
    if (deleted || parentDeleted) setShowFooter(false);
  }, [deleted, parentDeleted]);

  const switchDotsMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setShowDotsMenu(true);
  };

  const [helperDiv, setHideHelperDiv] = useState(false);

  useEffect(() => {
    let timeoutId: any;

    if (showFooter && lastReply) {
      timeoutId = setTimeout(() => {
        setHideHelperDiv(true);
      }, 100);
    } else {
      clearTimeout(timeoutId);
      setHideHelperDiv(false);
    }

    return () => clearTimeout(timeoutId);
  }, [showFooter, lastReply]);

  const hiddenHelperDiv = useMemo(() => {
    if (!helperDiv && lastReply) {
      return <div style={{ marginBottom: '24px' }} />;
    } else {
      return null;
    }
  }, [helperDiv, lastReply]);

  const closeFooter = () => {
    setShowFooter(false);
  };

  const onEditClose = () => {
    setIsEdit(false);
    setNewMsg('');
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const handleEdit = () => {
    setShowDotsMenu(false);
    setNewMsg(comment.message);
    setIsEdit(!isEdit);
  };

  const focusOnInput = () => {
    if (footerRef && footerRef.current) {
      const inputRef = footerRef.current.querySelector('textarea');
      if (inputRef) {
        inputRef.focus();
      }
    }
  };

  const [marginTop, setMarginTop] = useState('200px');
  useEffect(() => {
    if (showFooter) {
      setTimeout(() => {
        setMarginTop('30px');
      }, 50);
    } else {
      setMarginTop('200px');
    }
  }, [showFooter]);

  if (deleted) return null;

  return (
    <>
      <div className={cx({ [styles.commentWrapper]: true, [styles.commentSmall]: small })}>
        {comment ? (
          <div className={styles.comment}>
            <div className={styles.commentHeader}>
              <div className={styles.user}>
                <Avatar
                  avatar={comment?.created_by?.avatar_url}
                  className={cx(styles.avatar, { [styles.avatarSmall]: small })}
                  name={comment?.created_by?.username}
                />

                <div className={styles.userName}>
                  <div className={styles.name}>{comment?.created_by?.username}</div>
                </div>
              </div>
              <div className={styles.actionBtns}>
                {showDeleteBtn && (
                  <button type="button" className={styles.dotsMenuBtn} onClick={switchDotsMenu} disabled={isEdit} aria-label="Reply actions">
                    <div className={styles.dotIcon}>
                      <MoreIcon />
                    </div>
                  </button>
                )}
              </div>
            </div>
            {comment.quoted_parent_text && <blockquote className={styles.quotedText}>{comment.quoted_parent_text}</blockquote>}
            {isEdit ? (
              <div className={cx(styles.editTextBlock, styles.mainCommentEditTextWrapper)}>
                <div ref={footerRef} className={styles.editTextWrapper}>
                  <textarea aria-label="Edit reply text" className={styles.editText} onChange={onMsgEdit} value={newMsg}
                    rows={Math.min(6, Math.max(2, newMsg.split('\n').length))} />
                  <button onClick={focusOnInput} type="submit" className={styles.footerButton}>
                    <ArrowUpIcon onClick={onMsgSubmit} />
                  </button>
                </div>
                <img className={styles.removeChip} src={CrossIcon} alt="close" onClick={onEditClose} />
              </div>
            ) : (
              <div className={styles.commentText}>{comment?.message}</div>
            )}
            <div className={styles.date}>
              <div>{DateFormatter(getCommentDate(comment), true)}</div>
            </div>
            {!parentDeleted && (showFooter && lastReply ? (
              <div className={styles.replyInputContainer} style={{ marginTop }}>
                <Footer
                  showFooter={showFooter}
                  commentData={comment}
                  commentId={comment.id}
                  videofeedId={videofeedId}
                  fetch={fetch}
                  reply
                  setShowFooter={setShowFooter}
                  videoPlayedSeconds={videoPlayedSeconds}
                  small={small}
                  setImpertivlyShowFooter={setImpertivlyShowFooter}
                />
                <img className={styles.removeChip} src={CrossIcon} alt="close" onClick={closeFooter} />
              </div>
            ) : (
              <div className={styles.buttonWrapper}>
                <div className={styles.replyBtn}>
                  <button
                    type="button"
                    disabled={disable}
                    className={disable ? styles.commentButtonDisabled : styles.commentButton}
                    onClick={handleShowFooter}
                  >
                    <ArrowIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
        {!parentDeleted && hiddenHelperDiv}
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          style={{
            position: 'absolute',
            display: showDotsMenu ? 'block' : 'none',
            top: `45px`,
            right: `25px`,
          }}
        >
          <div className={styles.menuOption} onClick={handleEdit}>
            <EditButton /> Edit
          </div>
          <div className={styles.menuOption} onClick={handleCommentDelete}>
            <TrashButton /> Delete
          </div>
        </div>
      </div>
    </>
  );
};

export default CommentReply;
